import { Component, OnInit, ViewChild, ViewContainerRef } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
  DialogContentBase,
  DialogRef,
  DialogResult,
} from "@progress/kendo-angular-dialog";
import { concatMap, from, map, tap } from "rxjs";
import { UiNotificationService } from "src/app/external-ui-shared/services/ui-notification.service";
import { Claim } from "src/app/feature-module/claims-data/models/Claim";
import { ClaimDataService } from "src/app/feature-module/claims-data/service/claim-data.service";
import { CustomDialogSettings } from "src/app/shared/model/CustomDialogSettings";
import { CustomDialogService } from "src/app/shared/services/custom-dialog.service";
import { AudanetOperationType } from "../../models/AudanetOperationType";
import { AudanetTaskSearchResultItem } from "../../models/AudanetTaskSearchResultItem";
import { SearchAudanetTasksCriteriaFormBuilder } from "../../models/SearchAudanetTasksCriteria";
import { AudanetIntegrationService } from "../../service/audanet-integration.service";
import { ShowAssociatedAudanetTasksComponent } from "../show-associated-audanet-tasks/show-associated-audanet-tasks.component";
import { AudanetTask } from "../../models/AudanetTask";
import { ExportDocumentItem } from "src/app/feature-module/documents/models/ExportDocumentItem";
import { AudanetTaskAttachment } from "../../models/AudanetTaskAttachment";
import { AudanetTaskAttachments } from "../../models/AudanetTaskAttachments";
import { FileTypeService } from "src/app/shared/services/file-type.service";
import { DatePipe } from "@angular/common";

@Component({
  selector: "app-export-documents-to-audanet",
  templateUrl: "./export-documents-to-audanet.component.html",
  styleUrls: ["./export-documents-to-audanet.component.scss"],
})
export class ExportDocumentsToAudanetComponent
  extends DialogContentBase
  implements OnInit
{
  constructor(
    public dialog: DialogRef,
    private audanetIntegrationService: AudanetIntegrationService,
    private uiNotification: UiNotificationService,
    private customDialogService: CustomDialogService,
    private translateService: TranslateService,
    private claimDataService: ClaimDataService,
    private fileTypeService: FileTypeService
  ) {
    super(dialog);
  }

  exportDocumentItems: ExportDocumentItem[];
  exportDocumentItemsWithoutMappings: ExportDocumentItem[] = [];
  audanetTaskAttachments: AudanetTaskAttachment[] = [];
  claimIdentifier: string;
  isLoading: boolean;
  selectedTaskId: string;
  selectedTaskInfo: AudanetTaskSearchResultItem;
  selectedTaskData: AudanetTask;
  claim: Claim;

  @ViewChild("audanetExportDocumentDialog", { read: ViewContainerRef })
  public containerRef: ViewContainerRef;

  ngOnInit(): void {
    if (!this.claimIdentifier) {
      return;
    }

    this.isLoading = true;
    this.claimDataService
      .getClaimData(this.claimIdentifier)
      .pipe(tap(() => (this.isLoading = false)))
      .subscribe((res) => (this.claim = res));
  }

  selectAudanetTask() {
    const searchCriteria =
      SearchAudanetTasksCriteriaFormBuilder.initSearchAudanetTasksCriteria(
        this.claim.claimNumber,
        this.claim.carPlateNumber,
        this.claim.carVin
      );

    const dialogSettings: CustomDialogSettings = {
      title: this.translateService.instant(
        "claims-center.audanetIntegration.selectTaskOnAudanet"
      ),
      content: ShowAssociatedAudanetTasksComponent,
      minHeight: "50vh",
      minWidth: "50vw",
      maxHeight: "80vh",
      maxWidth: "80vw",
      appendTo: this.containerRef,
      dialogInputs: {
        searchCriteria: searchCriteria,
        audanetOperationType: AudanetOperationType.SelectionOfAudanetTask,
      },
      resultCallback: (r: DialogResult) => {
        if (r["text"] == "OK" && r["selectedTask"]) {
          this.selectedTaskInfo = r[
            "selectedTask"
          ] as AudanetTaskSearchResultItem;
          this.selectedTaskId = this.selectedTaskInfo.taskId;

          this.isLoading = true;
          this.audanetIntegrationService
            .getTask(this.selectedTaskId)
            .subscribe({
              next: (result) => {
                this.selectedTaskData = result;
                this.isLoading = false;
                this.mapDocumentsToAudanetAttachments();
              },
              error: () => {
                this.isLoading = false;
              },
            });
        }
      },
    };

    this.customDialogService.showDialog(dialogSettings);
  }

  mapDocumentsToAudanetAttachments() {
    const datePipe: DatePipe = new DatePipe("pl-PL");
    this.audanetIntegrationService
      .getMappingDictionary(this.selectedTaskData.insuranceCompanyAudanetId)
      .subscribe({
        next: (mappings) => {
          this.audanetTaskAttachments.splice(
            0,
            this.audanetTaskAttachments.length
          );
          this.exportDocumentItems.forEach((doc) => {
            const mapping = mappings.find(
              (x) => x.documentKindId == doc.documentKindId
            );

            const ts = datePipe.transform(new Date(), 'HHmmss')

            if (mapping) {
              const audanetAttachment: AudanetTaskAttachment = {
                fileName: doc.targetFileName.replace('.','_'+ ts +'.'),
                documentInfoId: doc.documentId,
                documentKindId: doc.documentKindId,
                audanetCategoryId: mapping.audanetCategory,
                audanetCategoryName: mapping.audanetCategoryName,
                isPhoto: this.fileTypeService.isFileImage(doc.fileExtension),
              };
              this.audanetTaskAttachments.push(audanetAttachment);
            } else {
              this.exportDocumentItemsWithoutMappings.push(doc);
            }
          });
        },
      });
  }

  exportToAudanet() {
    const chunkSize = 10;
    const arrayOfChunks = [];

    for (let i = 0; i < this.audanetTaskAttachments.length; i += chunkSize) {
      const chunk = this.audanetTaskAttachments.slice(i, i + chunkSize);
      arrayOfChunks.push(chunk);
    }

    const source = from(arrayOfChunks);

    source
      .pipe(
        concatMap((attachmentsChunk: AudanetTaskAttachment[]) => {
          this.isLoading = true;
          const req: AudanetTaskAttachments = {
            taskId: this.selectedTaskId,
            audanetTaskAttachmentsList: attachmentsChunk,
          };

          return this.audanetIntegrationService.addAttachments(req);
        }),
        map((response, index: number) => { return {response : response, request: arrayOfChunks[index] } }),
        tap(() => (this.isLoading = false))
      )
      .subscribe({
        next: (result) => {
          result.request.forEach((att) => {
            this.audanetTaskAttachments.find((x) => x.documentInfoId == att.documentInfoId).transferToAudanetResult = true;
          });
        },
        error: (error) => {
          console.log("Batch processing error", error);
        },
      });
  }

  exportToAudanet_old() {
    const request: AudanetTaskAttachments = {
      taskId: this.selectedTaskId,
      audanetTaskAttachmentsList: this.audanetTaskAttachments,
    };

    this.isLoading = true;

    this.audanetIntegrationService
      .addAttachments(request)
      .pipe(tap(() => (this.isLoading = false)))
      .subscribe((res) => {
        this.uiNotification.showSuccess(
          "operations.documentsSuccessfullyUploadedToAudanet"
        );
        this.dialog.close({ text: "OK" });
      });
  }
}
